/**
 * Program Guide — BroadcastFlow AI (dark theme)
 *
 * Self-contained TV-guide style schedule view, now wired to the
 * `/api/public/program-guide/by-slug/{slug}` backend endpoint instead of
 * mock JSON. Slug resolves from `?station=…` query param → last-visited
 * slug in localStorage → the logged-in user's active station.
 *
 * Routes:
 *   /guide           — main schedule grid + coming-up + AI suggestion
 *   /guide/reminders — list of shows the user set reminders for
 *   /guide/admin     — schedule manager (auth-required, real CRUD)
 *
 * Reminders persist to localStorage so they survive a page refresh.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  Bell, User, Clock, Bookmark, Sparkles, Tv,
  ChevronRight, Check, RefreshCw, LayoutDashboard,
  Play, Pause, X, MessageCircle, BellRing,
} from "lucide-react";
import {
  resolveSlugFromUrl, rememberSlug, fetchMyStationSlug,
  fetchPublicGuide, findProgramByIdInChannels,
  nextOccurrenceMs, hasTuneIn, addTuneIn, removeTuneIn, gcTuneIns,
} from "@/lib/programGuideClient";
import { appAlert } from "@/components/AppDialogHost";

// ============================================================
// CONSTANTS (TIME GRID + UI)
// ============================================================
const TIME_SLOTS = [12, 13, 14, 15, 16, 17]; // 12:00–17:00 GMT

// Dark-theme genre tints — muted backgrounds, saturated accent text
const COLOR_CLASSES = {
  news:  { bg: "bg-[#0d1e35]", text: "text-[#4a9eff]", border: "border-[#1e3050]" },
  music: { bg: "bg-[#0d2318]", text: "text-[#3ecf8e]", border: "border-[#1f3a2a]" },
  talk:  { bg: "bg-[#1a1230]", text: "text-[#a78bfa]", border: "border-[#2c1f4a]" },
  sport: { bg: "bg-[#241a08]", text: "text-[#f59e0b]", border: "border-[#3a2a10]" },
};

// 7-day-of-week selector (UTC; auto-picks today on load)
const DAY_OPTIONS = [
  { id: "mon", label: "Mon" },
  { id: "tue", label: "Tue" },
  { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" },
  { id: "fri", label: "Fri" },
  { id: "sat", label: "Sat" },
  { id: "sun", label: "Sun" },
];

function todayDayKey() {
  // JavaScript: Sun=0, Mon=1, ... Sat=6 → align with backend mon-sun
  const idx = (new Date().getUTCDay() + 6) % 7; // shift so Mon=0
  return DAY_OPTIONS[idx].id;
}

const NAV_TABS = [
  { id: "guide",     label: "Program Guide", to: "/guide" },
  { id: "live",      label: "Live Now",      to: "/guide?tab=live" },
  { id: "channels",  label: "Channels",      to: "/guide?tab=channels" },
  { id: "reminders", label: "My Reminders",  to: "/guide/reminders" },
];

const REMINDERS_STORAGE_KEY = "bf_program_guide_reminders";

// ============================================================
// SHARED HOOKS
// ============================================================
function useReminders() {
  const [ids, setIds] = useState(() => {
    try {
      const raw = localStorage.getItem(REMINDERS_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });

  useEffect(() => {
    try {
      localStorage.setItem(REMINDERS_STORAGE_KEY, JSON.stringify(ids));
    } catch { /* storage blocked */ }
  }, [ids]);

  const toggle = (id) => {
    setIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };
  const remove = (id) => setIds((cur) => cur.filter((x) => x !== id));
  const has = (id) => ids.includes(id);
  return { ids, toggle, remove, has };
}

/** Resolve slug → fetch schedule. Returns `{slug, station, channels,
 * loading, error, refetch}`. */
export function useStationGuide(day) {
  const location = useLocation();
  const [slug, setSlug] = useState(() => resolveSlugFromUrl(location.search));
  const [station, setStation] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  const refetch = () => setTick((t) => t + 1);

  // If no slug from URL/localStorage, try to resolve from the logged-in user
  useEffect(() => {
    let cancelled = false;
    if (slug) return;
    (async () => {
      const fallback = await fetchMyStationSlug();
      if (!cancelled && fallback) setSlug(fallback);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchPublicGuide(slug, day)
      .then((data) => {
        if (cancelled) return;
        setStation(data.station || null);
        setChannels(data.channels || []);
        rememberSlug(slug);
      })
      .catch((e) => { if (!cancelled) setError(e.message || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, tick, day]);

  return { slug, station, channels, loading, error, refetch };
}

// ============================================================
// DARK PAGE SHELL  (header + tab nav)
// ============================================================
export function GuideShell({ children }) {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#0f1117] text-[#c8ccd8] font-sans antialiased">
      <style>{`
        @keyframes guide-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.3; transform: scale(0.85); }
        }
        .guide-pulse { animation: guide-pulse 1.2s ease-in-out infinite; }
        .guide-fadein { animation: guide-fadein 0.25s ease-out; }
        @keyframes guide-fadein { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
      `}</style>

      {/* Top bar */}
      <header className="bg-[#0f1117]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/guide" className="flex items-center gap-3" data-testid="guide-logo">
            <img
              src="/brand/broadcastflowai-logo.png"
              alt="BroadcastFlow AI"
              className="h-10 w-auto object-contain"
            />
            <div className="hidden sm:block">
              <div className="text-[15px] font-semibold leading-tight text-[#c8ccd8]">BroadcastFlow AI</div>
              <div className="text-[11px] text-[#4a5068] leading-tight">Station AI Hub</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div
              className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2a1a1a] border border-[#3d1f1f]"
              data-testid="onair-badge"
            >
              <span className="h-2 w-2 rounded-full bg-[#ff4444] guide-pulse" />
              <span className="text-[11px] font-medium text-[#ff6b6b] uppercase tracking-wider">On Air Now</span>
            </div>
            <Link
              to="/dashboard"
              data-testid="back-to-dashboard"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium text-[#7ec8ff] bg-[#1a3a5c] hover:bg-[#1e4d7a] border border-[#1e4d7a] transition whitespace-nowrap"
            >
              <LayoutDashboard className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <button
              type="button"
              data-testid="guide-bell"
              className="h-9 w-9 rounded-md flex items-center justify-center text-[#4a5068] hover:text-[#c8ccd8] hover:bg-[#0a0d14]"
              aria-label="Notifications"
            >
              <Bell className="h-4 w-4" />
            </button>
            <button
              type="button"
              data-testid="guide-user"
              className="h-9 w-9 rounded-full bg-[#0a0d14] border border-[#1e2130] flex items-center justify-center text-[#4a5068]"
              aria-label="User"
            >
              <User className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <nav className="max-w-7xl mx-auto px-6 border-b" style={{ borderColor: "#1e2130", borderBottomWidth: "0.5px" }}>
          <div className="flex gap-1 overflow-x-auto">
            {NAV_TABS.map((t) => {
              const isActive =
                (t.id === "guide" && location.pathname === "/guide" && !location.search.includes("tab=")) ||
                (t.id === "reminders" && location.pathname.startsWith("/guide/reminders")) ||
                (t.id === "live" && location.search.includes("tab=live")) ||
                (t.id === "channels" && location.search.includes("tab=channels"));
              return (
                <Link
                  key={t.id}
                  to={t.to}
                  data-testid={`tab-${t.id}`}
                  className={`relative px-4 py-3 text-[13px] font-medium transition whitespace-nowrap ${
                    isActive ? "text-[#4a9eff]" : "text-[#4a5068] hover:text-[#c8ccd8]"
                  }`}
                >
                  {t.label}
                  {isActive && (
                    <span className="absolute left-3 right-3 -bottom-px h-0.5 rounded-t bg-[#4a9eff]" />
                  )}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">{children || <Outlet />}</main>
    </div>
  );
}

// ============================================================
// PROGRAM CARD — one cell in the schedule grid
// ============================================================
function ProgramCard({ program, channel, slug, onOpen }) {
  const c = COLOR_CLASSES[program.color || channel.color] || COLOR_CLASSES.news;

  const handleClick = () => {
    // Fire-and-forget interest ping for "Top hours by listener interest"
    if (slug) {
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/program-guide/program-interest/${encodeURIComponent(slug)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ program_id: program.id, kind: "click" }),
          keepalive: true,
        }).catch(() => { /* silent */ });
      } catch { /* ignore */ }
    }
    // Open the detail modal so the viewer can listen live + read the blurb
    onOpen?.(program, channel);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={`program-${program.id}`}
      className={`relative ${c.bg} ${c.text} border ${c.border} rounded-[5px] px-[7px] py-1.5 cursor-pointer h-full overflow-hidden text-left transition hover:opacity-80 hover:scale-[1.02]`}
      style={{ gridColumn: `span ${program.duration}` }}
      title={program.summary || program.description}
    >
      {program.live && (
        <span
          className="absolute top-1.5 right-1.5 h-[5px] w-[5px] rounded-full bg-[#ff4444] guide-pulse"
          data-testid={`live-dot-${program.id}`}
        />
      )}
      <div className="text-[10px] font-medium leading-[1.3] truncate pr-3">{program.title}</div>
      <div className="text-[9px] opacity-70 mt-0.5">
        {String(program.start).padStart(2, "0")}:00 · {program.duration}h
      </div>
    </button>
  );
}

// ============================================================
// PROGRAM DETAIL MODAL — live audio + program info (broadcast embed)
// ============================================================
function TuneInButton({ program, station }) {
  const [scheduled, setScheduled] = useState(() => hasTuneIn(program.id));
  const [fireAt, setFireAt] = useState(() => {
    const all = JSON.parse(localStorage.getItem("bf_program_tune_ins") || "[]");
    return all.find((t) => t.program_id === program.id)?.fire_at_ms || null;
  });
  const [permState, setPermState] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );

  const schedule = async () => {
    // 1. Resolve next occurrence in UTC
    const fireAtMs = nextOccurrenceMs(program);
    if (!fireAtMs) {
      await appAlert("Couldn't find the next airing — please try again later.");
      return;
    }
    // 2. Ask for notification permission if not yet granted
    if (typeof Notification === "undefined") {
      await appAlert("Your browser doesn't support notifications.");
      return;
    }
    if (Notification.permission === "default") {
      const result = await Notification.requestPermission();
      setPermState(result);
      if (result !== "granted") {
        await appAlert("Notifications are blocked — please allow them in your browser settings to use Tune In.");
        return;
      }
    }
    if (Notification.permission === "denied") {
      await appAlert("Notifications are blocked. Allow them in your browser settings, then try again.");
      return;
    }
    // 3. Persist + schedule
    addTuneIn({
      program_id: program.id,
      station_slug: station?.slug || station?.station_id || "",
      station_name: station?.name || "",
      title: program.title,
      fire_at_ms: fireAtMs,
    });
    setFireAt(fireAtMs);
    setScheduled(true);
  };

  const cancel = () => {
    removeTuneIn(program.id);
    setScheduled(false);
    setFireAt(null);
  };

  // Format "in 2h 14m" or "Mon at 18:00" depending on distance
  const friendlyWhen = (ts) => {
    if (!ts) return "";
    const diff = ts - Date.now();
    if (diff < 0) return "now";
    const hrs = Math.floor(diff / 3600_000);
    const mins = Math.floor((diff % 3600_000) / 60_000);
    if (diff < 60_000) return "in <1m";
    if (hrs < 12) return `in ${hrs}h ${mins}m`;
    const d = new Date(ts);
    const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getUTCDay()];
    return `${dow} at ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")} GMT`;
  };

  if (scheduled) {
    return (
      <button
        type="button"
        onClick={cancel}
        data-testid="tune-in-cancel-btn"
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[12px] font-medium bg-[#0d2318] text-[#3ecf8e] border border-[#1f3a2a] hover:bg-[#0f2a1c] transition"
      >
        <Check className="h-3.5 w-3.5" />
        Tune-in scheduled · {friendlyWhen(fireAt)}
        <span className="text-[10px] text-[#3ecf8e] opacity-70 ml-2">(click to cancel)</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={schedule}
      disabled={permState === "denied"}
      data-testid="tune-in-btn"
      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[12px] font-medium bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border border-[#1e4d7a] transition disabled:opacity-50 disabled:cursor-not-allowed"
      title={permState === "denied" ? "Notifications are blocked in your browser settings" : "Get a browser notification when this show starts"}
    >
      <BellRing className="h-3.5 w-3.5" />
      Tune in when it starts
    </button>
  );
}

function ProgramDetailModal({ open, program, channel, station, onClose }) {
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = useState(false);
  const [streamUrl, setStreamUrl] = useState("");
  const [audioError, setAudioError] = useState("");
  // Guard against the original click that opened the modal landing on the
  // backdrop after React re-renders (which would immediately close it).
  // Only allow backdrop-click-to-close after the modal has been mounted
  // for at least one event-loop tick.
  const [armed, setArmed] = useState(false);

  // Look up the parent station's live stream URL via the public-station endpoint
  useEffect(() => {
    if (!open || !station?.slug) return;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/stations/by-slug/${encodeURIComponent(station.slug || station.station_id)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (cancelled || !d) return;
        const url = d.stream?.https || d.stream?.shoutcast || d.stream?.icecast || d.stream?.backup || "";
        setStreamUrl(url);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, station?.slug, station?.station_id]);

  // Reset state when closing
  useEffect(() => {
    if (!open) {
      try { audioRef.current?.pause(); } catch { /* ignore */ }
      setPlaying(false);
      setAudioError("");
      setArmed(false);
    } else {
      // Arm the backdrop-close listener after the next paint so the
      // originating click doesn't immediately dismiss the modal.
      const id = setTimeout(() => setArmed(true), 150);
      return () => clearTimeout(id);
    }
  }, [open]);

  if (!open || !program) return null;

  const c = COLOR_CLASSES[program.color || channel?.color] || COLOR_CLASSES.news;

  const toggle = async () => {
    if (!audioRef.current) return;
    setAudioError("");
    try {
      if (playing) { audioRef.current.pause(); setPlaying(false); }
      else { await audioRef.current.play(); setPlaying(true); }
    } catch {
      setAudioError("Stream is offline or blocked by your browser.");
      setPlaying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 guide-fadein"
      onClick={() => { if (armed) onClose(); }}
      data-testid="program-detail-modal"
    >
      <div
        className="bg-[#0a0d14] rounded-xl shadow-2xl max-w-lg w-full"
        style={{ border: "0.5px solid #1e2130" }}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className={`flex items-center justify-between px-5 py-4 ${c.bg}`}
          style={{ borderBottom: "0.5px solid #1e2130" }}
        >
          <div className="flex items-center gap-3 min-w-0">
            {program.live && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#2a1a1a] border border-[#3d1f1f]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#ff4444] guide-pulse" />
                <span className="text-[9px] uppercase tracking-wider text-[#ff6b6b] font-semibold">LIVE</span>
              </span>
            )}
            <div className="min-w-0">
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${c.text}`}>
                {channel?.name} · {channel?.type}
              </div>
              <h2 className="text-[15px] font-semibold text-[#c8ccd8] truncate">{program.title}</h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="program-detail-close"
            className="h-8 w-8 rounded-md text-[#4a5068] hover:bg-[#0f1117] hover:text-[#c8ccd8] flex items-center justify-center transition"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {/* Program details */}
          <div className="text-[11px] text-[#4a5068] inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              {program.start_time || `${String(program.start).padStart(2, "0")}:00`} – {program.end_time || `${String(program.start + program.duration).padStart(2, "0")}:00`} GMT
            </span>
            <span className="opacity-60">·</span>
            <span>{program.duration}h</span>
          </div>
          {(program.summary || program.description) && (
            <p className="text-[13px] text-[#c8ccd8] leading-relaxed">{program.summary || program.description}</p>
          )}

          {/* Live audio player */}
          <div
            className="bg-[#0f1117] rounded-lg p-4 flex items-center gap-4"
            style={{ border: "0.5px solid #1e2130" }}
            data-testid="program-detail-player"
          >
            <button
              type="button"
              onClick={toggle}
              disabled={!streamUrl}
              data-testid="program-detail-play-btn"
              className="h-12 w-12 rounded-full bg-[#4a9eff] text-[#0a0d14] flex items-center justify-center shrink-0 transition disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4a9eff]">
                {streamUrl ? (playing ? "Now Streaming" : "Listen Live") : "Stream offline"}
              </div>
              <div className="text-[12px] text-[#c8ccd8] truncate">{station?.name}</div>
              {audioError && <div className="text-[10px] text-[#ff6b6b] mt-1">{audioError}</div>}
            </div>
            {streamUrl && (
              <audio
                ref={audioRef}
                src={streamUrl}
                preload="none"
                onEnded={() => setPlaying(false)}
                onError={() => { setAudioError("Stream is offline or blocked by your browser."); setPlaying(false); }}
              />
            )}
          </div>

          {/* Tune in when it starts */}
          <TuneInButton program={program} station={station} />

          {/* Live chat — coming soon */}
          <div
            className="bg-[#0f1117] rounded-lg p-3.5"
            style={{ border: "0.5px dashed #2c1f4a" }}
            data-testid="program-detail-chat-stub"
          >
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-md bg-[#1a1230] text-[#a78bfa] flex items-center justify-center shrink-0">
                <MessageCircle className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[#a78bfa]">Live chat</div>
                <div className="text-[11px] text-[#4a5068] mt-0.5">Coming soon — listeners will be able to chat with the host directly from this card.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DAY SELECTOR — Mon-Sun pill row + clock
// ============================================================
function DaySelector({ active, onChange }) {
  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  const hh = String(time.getUTCHours()).padStart(2, "0");
  const mm = String(time.getUTCMinutes()).padStart(2, "0");
  const todayKey = todayDayKey();
  return (
    <div
      className="flex items-center justify-between flex-wrap gap-3 mb-5 px-4 py-3 rounded-[10px] bg-[#0a0d14] border"
      style={{ borderColor: "#1e2130", borderWidth: "0.5px" }}
      data-testid="day-selector"
    >
      <div className="flex gap-2 overflow-x-auto">
        {DAY_OPTIONS.map((d) => {
          const isActive = active === d.id;
          const isToday = todayKey === d.id;
          return (
            <button
              key={d.id}
              data-testid={`day-${d.id}`}
              onClick={() => onChange(d.id)}
              type="button"
              className={`px-3.5 py-1.5 text-[12px] font-medium rounded-full border whitespace-nowrap transition flex items-center gap-1 ${
                isActive
                  ? "bg-[#1a3a5c] text-[#4a9eff] border-[#1e4d7a]"
                  : "bg-[#0f1117] text-[#4a5068] border-[#1e2130] hover:text-[#c8ccd8] hover:border-[#2a3050]"
              }`}
            >
              {d.label}
              {isToday && !isActive && <span className="h-1 w-1 rounded-full bg-[#4a9eff]" />}
            </button>
          );
        })}
      </div>
      <div className="inline-flex items-center gap-2 text-[12px] text-[#4a5068]" data-testid="current-time">
        <Clock className="h-3.5 w-3.5" />
        {hh}:{mm} GMT
      </div>
    </div>
  );
}

// ============================================================
// CHANNEL GRID — the TV-guide style schedule
// ============================================================
function ChannelGrid({ channels, slug, onOpenProgram }) {
  const cols = `88px repeat(${TIME_SLOTS.length}, minmax(120px, 1fr))`;
  return (
    <section
      className="rounded-[10px] overflow-x-auto"
      style={{ border: "0.5px solid #1e2130" }}
      data-testid="channel-grid"
    >
      {/* Time header row */}
      <div
        className="grid bg-[#0a0d14] px-1 py-2"
        style={{ gridTemplateColumns: cols, columnGap: "8px", borderBottom: "0.5px solid #1e2130" }}
      >
        <div className="text-[10px] uppercase tracking-wider text-[#4a5068] font-semibold px-1">Channel</div>
        {TIME_SLOTS.map((h) => (
          <div key={h} className="text-[10px] text-[#4a5068] font-medium px-1" data-testid={`time-${h}`}>
            {String(h).padStart(2, "0")}:00
          </div>
        ))}
      </div>

      {/* Channel rows */}
      <div className="bg-[#0f1117]">
        {channels.map((ch, idx) => (
          <div
            key={ch.id}
            className="grid items-stretch"
            style={{
              gridTemplateColumns: cols,
              columnGap: "3px",
              padding: "4px",
              minHeight: "54px",
              borderTop: idx === 0 ? "none" : "0.5px solid #1e2130",
            }}
            data-testid={`channel-row-${ch.id}`}
          >
            {/* Channel label */}
            <div
              className="px-2 self-center bg-[#0a0d14] rounded-[5px] py-2"
              style={{ borderRight: "0.5px solid #1e2130" }}
            >
              <div className="text-[11px] font-medium text-[#c8ccd8] leading-tight">{ch.name}</div>
              <div className="text-[10px] text-[#4a5068] mt-0.5">{ch.type}</div>
            </div>

            {/* Program cells */}
            {(ch.programs || []).map((p) => (
              <ProgramCard key={p.id} program={p} channel={ch} slug={slug} onOpen={onOpenProgram} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================
// UPCOMING CARD — "Coming up next" with reminder toggle
// ============================================================
function UpcomingCard({ program, channelName, channelType, hasReminder, onToggle }) {
  return (
    <div
      className="bg-[#0a0d14] rounded-[10px] p-[11px] flex flex-col gap-2 hover:border-[#2a3050] transition"
      style={{ border: "0.5px solid #1e2130" }}
      data-testid={`upcoming-${program.id}`}
    >
      <div className="text-[10px] uppercase tracking-[0.04em] text-[#4a5068] font-semibold">
        {channelName} · {channelType}
      </div>
      <div className="text-[12px] font-medium text-[#c8ccd8] leading-tight">{program.title}</div>
      <div className="text-[11px] text-[#4a5068]">{program.summary || program.description}</div>
      <div className="flex items-center justify-between mt-1">
        <div className="inline-flex items-center gap-1.5 text-[10px] text-[#4a5068]">
          <Clock className="h-3 w-3" />
          {String(program.start).padStart(2, "0")}:00 GMT
        </div>
        <button
          type="button"
          onClick={() => onToggle(program.id)}
          data-testid={`reminder-toggle-${program.id}`}
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium border transition ${
            hasReminder
              ? "bg-[#1a3a5c] text-[#7ec8ff] border-[#2a5a8c]"
              : "bg-[#0d1e35] text-[#4a9eff] border-[#1e4d7a] hover:bg-[#142a48]"
          }`}
          style={{ transitionDuration: "0.15s" }}
        >
          {hasReminder ? <><Check className="h-3 w-3" /> Set</> : <><Bookmark className="h-3 w-3" /> Remind me</>}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// AI SUGGESTION BAR
// ============================================================
function AISuggestionBar({ onApply }) {
  return (
    <div
      className="mt-6 bg-[#0a0d14] rounded-[10px] p-4 flex items-center gap-4"
      style={{ border: "0.5px solid #1e3050", margin: "14px 16px" }}
      data-testid="ai-suggestion-bar"
    >
      <div className="h-7 w-7 rounded-[6px] bg-[#1a3a5c] text-[#4a9eff] flex items-center justify-center shrink-0">
        <Sparkles className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#4a5068] font-semibold">AI schedule suggestion</div>
        <div className="text-[11px] text-[#c8ccd8] leading-tight mt-1">
          Add a <strong className="text-[#4a9eff]">&ldquo;Tech &amp; AI Weekly&rdquo;</strong> show on Talk Live — Sundays 18:00 get
          <strong className="text-[#4a9eff]"> 3× more viewers</strong> on your platform.
        </div>
      </div>
      <button
        type="button"
        onClick={onApply}
        data-testid="apply-ai-suggestion"
        className="px-4 py-2 rounded-md border border-[#1e4d7a] text-[#4a9eff] bg-transparent hover:bg-[#0d1e35] text-[11px] font-medium whitespace-nowrap transition shrink-0"
      >
        Apply suggestion
      </button>
    </div>
  );
}

// ============================================================
// EMPTY / LOADING / ERROR STATES
// ============================================================
function StateBlock({ icon: Icon, title, subtitle, action }) {
  return (
    <div
      className="bg-[#0a0d14] rounded-[10px] p-10 text-center"
      style={{ border: "0.5px dashed #1e2130" }}
      data-testid="guide-state-block"
    >
      <div className="h-12 w-12 mx-auto rounded-full bg-[#0d1e35] text-[#4a9eff] flex items-center justify-center mb-3">
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-[14px] font-medium text-[#c8ccd8]">{title}</div>
      {subtitle && <p className="text-[12px] text-[#4a5068] mt-1 max-w-md mx-auto">{subtitle}</p>}
      {action}
    </div>
  );
}

// ============================================================
// MAIN PROGRAM GUIDE PAGE
// ============================================================
export default function Guide() {
  const [activeDay, setActiveDay] = useState(() => todayDayKey());
  const [openProgram, setOpenProgram] = useState(null);   // { program, channel }
  const reminders = useReminders();
  const { slug, station, channels, loading, error, refetch } = useStationGuide(activeDay);

  const handleOpenProgram = (program, channel) => {
    setOpenProgram({ program, channel });
  };

  // ─── Tune-in dispatcher ─────────────────────────────────────
  // Runs once per mount. For every saved tune-in within the next 6 hours,
  // schedule a setTimeout that fires a browser notification. Anything
  // further out will be re-scheduled on the next Guide visit. (We avoid
  // scheduling 24h timeouts because tabs commonly throttle / sleep.)
  useEffect(() => {
    gcTuneIns(); // drop expired
    const upcoming = JSON.parse(localStorage.getItem("bf_program_tune_ins") || "[]");
    const SIX_HOURS = 6 * 60 * 60 * 1000;
    const timers = [];
    for (const t of upcoming) {
      const delay = t.fire_at_ms - Date.now();
      if (delay <= 0 || delay > SIX_HOURS) continue;
      const id = setTimeout(() => {
        try {
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            const n = new Notification(`On Air now: ${t.title}`, {
              body: `${t.station_name || "Your station"} is starting "${t.title}". Tap to listen.`,
              tag: `bf-tune-in-${t.program_id}`,
            });
            n.onclick = () => {
              window.focus();
              window.location.href = `/r/${t.station_slug}`;
              try { n.close(); } catch { /* noop */ }
            };
          }
          removeTuneIn(t.program_id);
        } catch { /* ignore */ }
      }, delay);
      timers.push(id);
    }
    return () => timers.forEach(clearTimeout);
  }, []);

  // Update document title for SEO
  useEffect(() => {
    if (station?.name) document.title = `${station.name} — Program Guide`;
  }, [station?.name]);

  const upcoming = useMemo(() => {
    const all = [];
    for (const c of channels) {
      for (const p of (c.programs || [])) {
        if (!p.live && p.start >= 15) {
          all.push({ ...p, channelName: c.name, channelType: c.type });
        }
      }
    }
    return all.sort((a, b) => a.start - b.start).slice(0, 3);
  }, [channels]);

  return (
    <GuideShell>
      {/* Page heading */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-4">
          <div
            className="h-12 w-12 rounded-lg flex items-center justify-center bg-[#0d1e35] shrink-0"
            style={{ border: "0.5px solid #1e4d7a", boxShadow: "0 0 24px -4px #4a9eff66" }}
          >
            <Tv className="h-6 w-6 text-[#4a9eff]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4a9eff] font-semibold mb-1">
              // Program Guide{station?.name ? ` · ${station.name}` : ""}
            </div>
            <h1
              className="text-3xl sm:text-4xl font-bold text-[#f0f2f8] tracking-tight leading-tight"
              data-testid="guide-page-title"
            >
              What&apos;s on across BroadcastFlow
            </h1>
            <p className="text-[12px] text-[#4a5068] mt-1.5">
              Watch live, set reminders for upcoming shows, and let AI schedule the rest.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={refetch}
            disabled={loading}
            data-testid="guide-refresh"
            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-md text-[12px] font-medium text-[#4a5068] hover:text-[#c8ccd8] hover:bg-[#0a0d14] border border-[#1e2130] transition disabled:opacity-50"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <Link
            to="/guide/admin"
            data-testid="admin-link"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-[12px] font-medium bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border border-[#1e4d7a] transition whitespace-nowrap"
          >
            <Tv className="h-4 w-4" /> Schedule Manager
          </Link>
        </div>
      </div>

      {/* Body: loading / error / empty / loaded */}
      {loading && channels.length === 0 ? (
        <StateBlock
          icon={Tv}
          title="Loading schedule…"
          subtitle="Fetching your station's programs."
        />
      ) : error ? (
        <StateBlock
          icon={Tv}
          title="Couldn't load schedule"
          subtitle={error}
          action={
            <button
              type="button"
              onClick={refetch}
              data-testid="retry-fetch"
              className="inline-flex items-center mt-4 px-4 py-2 rounded-md bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border border-[#1e4d7a] text-[12px] font-medium transition"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Try again
            </button>
          }
        />
      ) : !slug ? (
        <StateBlock
          icon={Tv}
          title="No station selected"
          subtitle="Open this page from your dashboard, or append ?station=your-slug to the URL."
          action={
            <Link
              to="/login"
              className="inline-flex items-center mt-4 px-4 py-2 rounded-md bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border border-[#1e4d7a] text-[12px] font-medium transition"
              data-testid="login-prompt"
            >
              Sign in to view your schedule
            </Link>
          }
        />
      ) : channels.length === 0 ? (
        <StateBlock
          icon={Tv}
          title="No programs scheduled yet"
          subtitle="Add your first channels and programs in Schedule Manager — or seed a starter lineup with one click."
          action={
            <Link
              to="/guide/admin"
              className="inline-flex items-center mt-4 px-4 py-2 rounded-md bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border border-[#1e4d7a] text-[12px] font-medium transition"
              data-testid="empty-cta-admin"
            >
              <Tv className="h-3.5 w-3.5 mr-1.5" /> Open Schedule Manager
            </Link>
          }
        />
      ) : (
        <>
          <DaySelector active={activeDay} onChange={setActiveDay} />
          <ChannelGrid channels={channels} slug={slug} onOpenProgram={handleOpenProgram} />

          {/* Coming up next */}
          {upcoming.length > 0 && (
            <section className="mt-6" data-testid="coming-up-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-[13px] font-medium text-[#c8ccd8] uppercase tracking-wider">Coming up next</h2>
                <Link
                  to="/guide/reminders"
                  className="text-[11px] text-[#4a9eff] hover:underline inline-flex items-center gap-1"
                  data-testid="see-all-reminders"
                >
                  My reminders <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {upcoming.map((p) => (
                  <UpcomingCard
                    key={p.id}
                    program={p}
                    channelName={p.channelName}
                    channelType={p.channelType}
                    hasReminder={reminders.has(p.id)}
                    onToggle={reminders.toggle}
                  />
                ))}
              </div>
            </section>
          )}

          {/* AI suggestion */}
          <AISuggestionBar onApply={() => { window.location.assign("/guide/admin?prefill=ai"); }} />
        </>
      )}

      {/* Footer */}
      <div className="mt-8 text-center text-[11px] text-[#4a5068]">
        © BroadcastFlow AI · Station AI Hub
      </div>

      {/* Broadcast embed — live audio + program details */}
      <ProgramDetailModal
        open={!!openProgram}
        program={openProgram?.program}
        channel={openProgram?.channel}
        station={station}
        onClose={() => setOpenProgram(null)}
      />
    </GuideShell>
  );
}

// Re-export for GuideReminders.jsx
export { findProgramByIdInChannels };
